import React from 'react';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Code, Text, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { AuthLayout } from '@/components/authentication/ui/AuthLayout.tsx';

interface ErrorPageProps {
	error?: Error;
	resetErrorBoundary?: () => void;
	showReportButton?: boolean;
	eventId?: string | null;
}

/**
 * Full-page error display shown when the application encounters an error.
 * Uses AuthLayout for consistent styling with authentication pages.
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
	error,
	resetErrorBoundary,
	showReportButton = true,
	eventId,
}) => {
	return (
		<AuthLayout title="Hit a snag">
			<VStack gap={4} w="full" minH="600" align="center">
				<Text fontSize="sm" color="fg.muted" textAlign="center" mt={6}>
					We're sorry for the inconvenience.
				</Text>

				{import.meta.env.DEV && error && (
					<Code
						p={4}
						borderRadius="2xl"
						w="full"
						// TODO: chakra-migration token.grayRaw — a dev-only stack trace, deliberately darker
						// than fg.muted; it is body text in a code block rather than the muted role
						color="gray.700"
					>
						{error.message}
					</Code>
				)}

				{showReportButton && eventId && (
					<OutlinedButton
						w="full"
						onClick={() => {
							if (eventId) {
								Sentry.showReportDialog({ eventId });
							} else {
								Sentry.showReportDialog();
							}
						}}
					>
						Report feedback
					</OutlinedButton>
				)}

				<PrimaryButton
					onClick={() => {
						if (resetErrorBoundary) {
							resetErrorBoundary();
						} else {
							window.location.href = '/';
						}
					}}
				>
					Back to Dashboard
				</PrimaryButton>
			</VStack>
		</AuthLayout>
	);
};
